import React from 'react';

import { styled, SxProps, Theme, useTheme } from '@mui/material/styles';

import '../../node_modules/@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';

import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useIsUp } from '@im/ui-lib/responsive';
import { usePageTitle } from '../config';
import { IngramAppLinks } from './ingramAppLinks';
import { AppQrCode } from './appQrCode';
import { CulturePickerZenith } from '@im/ui-lib/zenith/CulturePicker';

type Props = {
  children: JSX.Element;
  logo: React.ReactNode;
  primaryAuthTitle: string;
  showSplashBp?: 'md' | 'lg';
  splashContent: React.ReactNode;
  splashSx?: SxProps<Theme>;
};

export const LoginLayoutZenith = ({
  children,
  logo,
  primaryAuthTitle,
  showSplashBp = 'md',
  splashContent,
  splashSx,
}: Readonly<Props>) => {
  const translateStr = useTranslate();
  const { updatePageTitle } = usePageTitle();
  updatePageTitle(translateStr('Title.Login'));

  const copyrightContent = translateStr('OktaWidget.Copyright');

  const whatYearIsItNow = new Date().getFullYear().toString();

  const showSplash = useIsUp(showSplashBp);

  return (
    <StyledGridContainer
      columnSpacing={8}
      container
      height="100vh"
      paddingLeft={8}
    >
      <Grid
        alignItems="center"
        display="flex"
        flexDirection="column"
        item
        justifyContent="space-between"
        lg={showSplash ? 6 : undefined}
        md={showSplash ? 6 : undefined}
        mr={showSplash ? 0 : 8}
        sx={{
          pb: 8,
          pt: 8,
        }}
        xs={12}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          width="100%"
        >
          <Stack height={40} justifyContent="center">
            {logo}
          </Stack>
          <CulturePickerZenith />
        </Stack>

        <Box marginTop={12}>
          <Typography variant="display-sm-semibold">
            {primaryAuthTitle}
          </Typography>
          {children}
        </Box>

        <Box
          alignItems="flex-end"
          display="flex"
          justifyContent="space-between"
          pt={8}
          width="100%"
        >
          <Typography
            color={(theme) => theme.palette.gray[600]}
            variant="text-sm-regular"
          >
            {copyrightContent.replace(/\{\{year\}\}/gi, whatYearIsItNow)}
          </Typography>

          {!showSplash && <DownloadBadgesAndQR />}
        </Box>
      </Grid>

      {showSplash && (
        <Grid
          item
          lg={showSplash ? 6 : undefined}
          md={showSplash ? 6 : undefined}
          sx={{
            alignItems: 'center',
            display: 'flex',
            pb: 4,
            pr: 4,
            pt: 4,
          }}
          xs={0}
        >
          <Stack
            alignItems="center"
            height="100%"
            justifyContent="center"
            sx={{
              backgroundColor: 'primary.600',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '20px',
              overflow: 'hidden',
              ...splashSx,
            }}
            width="100%"
          >
            {splashContent}
          </Stack>
        </Grid>
      )}
      {window.isOneTrustEnabled && (
        <style>{`#onetrust-banner-sdk.otFlat {left:28px !important}`}</style>
      )}
    </StyledGridContainer>
  );
};

const StyledGridContainer = styled(Grid)(({ theme }) => ({
  '#okta-sign-in .auth-header': {
    padding: '0',
  },
  '#okta-sign-in .authenticator-row .authenticator-button .button': {
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.gray[400]}`,
    borderRadius: 15,
    color: theme.palette.gray[700],
  },
  '#okta-sign-in .beacon-container': {
    bottom: 50,
  },
  '#okta-sign-in.auth-container': {
    '.mfa-google-auth,.mfa-google-auth-30': {
      backgroundImage:
        'url(/cep/assets/okta-signin-widget/img/icons/mfa/google-ms-authenticators_sm.png)',
      backgroundSize: 'contain',
    },
  },
  '#okta-sign-in.auth-container.main-container': {
    '.auth-content': {
      margin: 0,
      padding: 'unset',
    },
    '.button-primary': {
      background: theme.palette.primary[600],
      borderColor: theme.palette.primary[600],
      borderRadius: 32,
      color: theme.palette.common.white,
      fontSize: 18,
      height: 44,
    },
    '.button-secondary': {
      background: theme.palette.common.white,
      border: `1px solid ${theme.palette.gray[400]}`,
      borderRadius: 32,
      color: theme.palette.gray[700],
    },
    '.link': {
      ...theme.typography['text-sm-semibold'],
      color: theme.palette.primary[700],
      textDecoration: 'none',
    },
    '.o-form .infobox': {
      color: theme.palette.text.secondary,
    },
    '.o-form .input-fix': {
      border: 'unset',
      height: 40,
      input: {
        '&:-webkit-autofill': {
          WebkitBoxShadow: `0 0 0 25px ${theme.palette.common.white} inset`,
          WebkitTextFillColor: theme.palette.gray[500],
        },
        '&::selection': {
          //backgroundColor: theme.palette.common.black,
        },
        border: `1px solid ${theme.palette.gray[400]}`,
        borderRadius: 8,
      },
    },
    '.o-form-explain': {
      color: 'unset',
      textAlign: 'left',
    },
    '.o-form-fieldset': {
      '&:first-of-type': {
        marginBottom: 20,
      },
    },
    '.o-form-head': {
      ...theme.typography['text-md-regular'],
      color: theme.palette.gray[600],
      textAlign: 'left',
    },
    '.okta-form-input-field': {
      backgroundColor: 'unset',
      boxShadow: 'unset',
    },
    '.okta-form-label': {
      color: theme.palette.gray[700],
      fontWeight: 500,
      position: 'relative',
      top: -4,
      width: 'auto',
      zIndex: 1,
    },
    '.okta-sign-in-header.auth-header': {
      '.beacon-container.beacon-small': {
        display: 'none',
      },
      borderBottom: 'unset',
    },

    backgroundColor: 'transparent',
    borderColor: 'transparent',
    boxShadow: 'none',
    color: theme.palette.text.primary,
    fontFamily: 'Inter',
    input: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text.primary,
      fontFamily: 'unset',
    },
    margin: '0',
    overflow: 'unset',
  },
  '#okta-sign-in.no-beacon .auth-header': {
    paddingBottom: 'unset',
  },
}));

const DownloadBadgesAndQR = () => {
  const translateStr = useTranslate();
  const theme = useTheme();
  return (
    <Stack alignItems={'center'} justifyContent={'center'} ml={4} spacing={2.5}>
      <Typography color="gray.600" variant="text-sm-medium">
        {translateStr('Login.MobileDownloadHeader')}
      </Typography>

      <Stack
        alignItems="center"
        direction="row"
        justifyContent="center"
        spacing={2}
      >
        <Stack
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'space-between'}
          spacing={2}
        >
          <IngramAppLinks width="145px" />
        </Stack>

        <AppQrCode
          size={100}
          style={{
            border: `0.5px solid ${theme.palette.gray[200]}`,
            padding: '2px',
          }}
        />
      </Stack>
    </Stack>
  );
};
